import { Badge, Button, Popover } from 'antd';
import React from 'react';
import { connectivityMonitor } from '../helpers/connectivityMonitor';
import { eventSubmissionQueue } from '../helpers/eventSubmissionQueue';
import { OutboxRecord } from '../helpers/offlineEventStore';

interface Props {
    matchId: number;
    /** Dark mobile report vs desktop. */
    variant?: 'mobile' | 'desktop';
}

interface State {
    online: boolean;
    pending: OutboxRecord[];
    open: boolean;
}

/**
 * Online/offline icon with badge; click opens pending outbox list for this match.
 */
class OfflineStatusControl extends React.Component<Props, State> {
    private unsubConnectivity: (() => void) | null = null;

    private unsubOutbox: (() => void) | null = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            online: connectivityMonitor.getIsOnline(),
            pending: [],
            open: false,
        };
    }

    public componentDidMount = (): void => {
        eventSubmissionQueue.start();
        this.unsubConnectivity = connectivityMonitor.subscribe((online: boolean) => {
            this.setState({ online });
        });
        this.unsubOutbox = eventSubmissionQueue.subscribe((e): void => {
            if (e.type === 'changed') {
                void this.refreshPending();
            }
        });
        void this.refreshPending();
    };

    public componentDidUpdate = (prev: Props): void => {
        if (prev.matchId !== this.props.matchId) {
            void this.refreshPending();
        }
    };

    public componentWillUnmount = (): void => {
        this.unsubConnectivity?.();
        this.unsubOutbox?.();
    };

    private refreshPending = async (): Promise<void> => {
        const pending = await eventSubmissionQueue.listByMatch(this.props.matchId);
        this.setState({ pending });
    };

    private ageLabel = (createdAt: number): string => {
        const sec = Math.max(0, Math.floor((Date.now() - createdAt) / 1000));
        if (sec < 60) return `${sec}s ago`;
        const min = Math.floor(sec / 60);
        if (min < 60) return `${min}m ago`;
        return `${Math.floor(min / 60)}h ago`;
    };

    private content = (): React.ReactElement => {
        const { pending, online } = this.state;
        return (
            <div className="offline-status-popover">
                <div className="offline-status-popover__head">
                    {online ? 'Online' : 'Offline'} · {pending.length} pending
                </div>
                {pending.length === 0 ? (
                    <div className="offline-status-popover__empty">No queued events.</div>
                ) : (
                    <ul className="offline-status-popover__list">
                        {pending.map((row) => (
                            <li key={row.id}>
                                <span className="offline-status-popover__op">{row.op}</span>
                                <span className="offline-status-popover__label">{row.label}</span>
                                <span className="offline-status-popover__age">
                                    {this.ageLabel(row.createdAt)}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
                <Button
                    size="small"
                    block
                    type="primary"
                    disabled={!online}
                    onClick={(e): void => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Shared by desktop + mobile: clears holds, resets attempt
                        // budget, probes Arena, drains outbox, then screens refetch.
                        eventSubmissionQueue.forceFlush();
                        this.setState({ open: false });
                    }}
                >
                    Sync now
                </Button>
            </div>
        );
    };

    public render = (): React.ReactElement => {
        const { variant = 'desktop' } = this.props;
        const { online, pending, open } = this.state;
        const count = pending.length;
        const icon = (
            <button
                type="button"
                className={`offline-status-btn offline-status-btn--${variant}${
                    online ? ' offline-status-btn--online' : ' offline-status-btn--offline'
                }`}
                aria-label={online ? 'Online' : 'Offline'}
                title={online ? 'Online' : 'Offline'}
            >
                <span className="offline-status-btn__dot" />
                <span className="offline-status-btn__text">{online ? 'Online' : 'Offline'}</span>
            </button>
        );

        return (
            <Popover
                content={this.content()}
                trigger="click"
                visible={open}
                onVisibleChange={(v): void => this.setState({ open: v })}
                placement="bottomRight"
            >
                <Badge count={count} size="small" offset={[-2, 2]}>
                    {icon}
                </Badge>
            </Popover>
        );
    };
}

export default OfflineStatusControl;
